import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useCategoryMappingAuth from "./useCategoryMappingAuth";
import { missingDefaultSites } from "./categoryDefaultConflicts";
import { isCancelledRead } from "./defaultConflictsApi";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import ModaArticoliAdvancedFiltersModal from "./ModaArticoliAdvancedFiltersModal";
import WebCategoryMappingCards from "./WebCategoryMappingCards";

const CATEGORY_MAPPING_MODA_FIELDS = [
  { key: "reparto", label: "Reparto", lookupUrl: "/moda/lookup/reparti" },
  { key: "categoria", label: "Categoria", lookupUrl: "/moda/lookup/categorie" },
  { key: "tipo", label: "Tipo", lookupUrl: "/moda/lookup/tipi" },
  { key: "sottotipo", label: "Sottotipo", lookupUrl: "/moda/lookup/sottotipi" },
  { key: "persona", label: "Persona", lookupUrl: "/moda/lookup/persona" },
];

const CATEGORY_MAPPING_MONGO_FIELDS = [
  { key: "stagioneConsigliata", label: "Stagione consigliata", path: "datiUlteriori.generici.stagioneConsigliata.valueId" },
  { key: "utilizzo", label: "Utilizzo (generico)", path: "datiUlteriori.generici.utilizzo.valueId" },
  { key: "calzatureUtilizzo", label: "Utilizzo (calzature)", path: "datiUlteriori.calzature.utilizzo.valueId" },
  { key: "sciUtilizzo", label: "Utilizzo (sci)", path: "datiUlteriori.sci.utilizzo.valueId" },
  { key: "skiBootsUtilizzo", label: "Utilizzo (scarponi sci)", path: "datiUlteriori.skiBoots.utilizzo.valueId" },
  { key: "bastoniSport", label: "Sport bastoni", path: "datiUlteriori.bastoni.sport.valueId" },
  { key: "bastoniUtilizzo", label: "Specialita sci bastoni", path: "datiUlteriori.bastoni.utilizzo.valueId" },
  { key: "bikeUtilizzo", label: "Utilizzo (bike)", path: "datiUlteriori.bike.utilizzo.valueId" },
  { key: "calzatureAspetto", label: "Aspetto calzature", path: "datiUlteriori.calzature.aspetto.valueId" },
  { key: "piscinaMareTipo", label: "Accessorio mare", path: "datiUlteriori.piscinaMare.tipo.valueId" },
  { key: "trekkingCampingAccessori", label: "Accessorio camping", path: "datiUlteriori.trekkingCamping.accessori.valueId" },
  { key: "arrampicataTipoAttrezzatura", label: "Attrezzatura arrampicata", path: "datiUlteriori.arrampicata.tipoAttrezzatura.valueId" },
  { key: "golfTipoAccessorioGolf", label: "Accessorio golf", path: "datiUlteriori.golf.tipoAccessorioGolf.valueId" },
];

const CATEGORY_MAPPING_REQUIREMENT_FIELDS = [
  ...CATEGORY_MAPPING_MODA_FIELDS.map((field) => ({
    key: `moda:${field.key}`,
    source: "moda",
    field: field.key,
    label: field.label,
  })),
  ...CATEGORY_MAPPING_MONGO_FIELDS.map((field) => ({
    key: `mongo:${field.path}`,
    source: "mongo",
    path: field.path,
    label: field.label,
  })),
];

const EMPTY_MODA_COMBINATION_FILTER = "__EMPTY__";

const CATEGORY_SITES = [
  { key: "VIG", label: "vigliettisport.com", color: "#174f88" },
  { key: "BOT", label: "botteroski.com", color: "#8b1e1e" },
];

const DEFAULT_FILTERS = {
  page: 1,
  perPage: 50,
  sortBy: "articleCount",
  sortDirection: "desc",
  search: "",
  publishedOnly: false,
  missingMappingOnly: false,
  defaultConflictsOnly: false,
  includeIgnored: false,
  minArticleCount: "",
  maxArticleCount: "",
  minStock: "",
  maxStock: "",
  reparto: "",
  categoria: "",
  tipo: "",
  sottotipo: "",
  persona: "",
};

const getFirstFilledValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const normalized = String(value).trim();
    if (normalized.length) return normalized;
  }
  return "";
};

const isValidAuditUsername = (value) =>
  Boolean(value) && !/^[A-Za-z0-9._-]+\|/.test(value);

const normalizeLookupRecords = (payload) => {
  const list = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  return list
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "string") {
        const value = item.trim();
        return value ? { code: value, description: value } : null;
      }
      const code =
        item.code ??
        item.codice ??
        item.cod ??
        item.id ??
        item.value ??
        item.key ??
        item.ID;
      if (code === undefined || code === null || String(code).trim() === "") {
        return null;
      }
      const description =
        item.description ??
        item.descrizione ??
        item.label ??
        item.nomeDisplay ??
        item.nome_display ??
        item.nome ??
        item.name ??
        item.desc ??
        code;
      return {
        code: String(code).trim(),
        description: String(description ?? code).trim(),
      };
    })
    .filter(Boolean);
};

const firstPositiveFilterValue = (filters, key) => {
  const eqValue = getFirstFilledValue(filters?.[`${key}_eq`]);
  if (eqValue) return eqValue;
  const inValues = Array.isArray(filters?.[`${key}_in`])
    ? filters[`${key}_in`].filter((value) => getFirstFilledValue(value))
    : [];
  return inValues.length ? inValues[0] : "";
};

const buildCategoryMappingConditions = (filters) => {
  const conditions = [];
  CATEGORY_MAPPING_MODA_FIELDS.forEach((field) => {
    const key = field.key;
    const eqValue = getFirstFilledValue(filters?.[`${key}_eq`]);
    const inValues = Array.isArray(filters?.[`${key}_in`])
      ? filters[`${key}_in`].filter((value) => getFirstFilledValue(value))
      : [];
    const notInValues = Array.isArray(filters?.[`${key}_not_in`])
      ? filters[`${key}_not_in`].filter((value) => getFirstFilledValue(value))
      : [];
    if (eqValue) {
      conditions.push({ source: "moda", field: key, operator: "eq", value: eqValue });
    } else if (notInValues.length) {
      conditions.push({ source: "moda", field: key, operator: "not_in", values: notInValues });
    } else if (inValues.length) {
      conditions.push({ source: "moda", field: key, operator: "in", values: inValues });
    }
  });
  CATEGORY_MAPPING_MONGO_FIELDS.forEach((field) => {
    const key = field.key;
    const inValues = Array.isArray(filters?.mongoFilters?.[`${key}_in`])
      ? filters.mongoFilters[`${key}_in`].filter((value) => getFirstFilledValue(value))
      : [];
    const notInValues = Array.isArray(filters?.mongoFilters?.[`${key}_not_in`])
      ? filters.mongoFilters[`${key}_not_in`].filter((value) => getFirstFilledValue(value))
      : [];
    if (notInValues.length) {
      conditions.push({ source: "mongo", path: field.path, operator: "not_in", values: notInValues });
    } else if (inValues.length) {
      conditions.push({ source: "mongo", path: field.path, operator: "in", values: inValues });
    }
  });
  return conditions;
};

const buildCategoryMappingResolveInput = (filters) => {
  const moda = {};
  const mongo = {};
  CATEGORY_MAPPING_MODA_FIELDS.forEach((field) => {
    const value = firstPositiveFilterValue(filters, field.key);
    if (value) moda[field.key] = value;
  });
  CATEGORY_MAPPING_MONGO_FIELDS.forEach((field) => {
    const value = firstPositiveFilterValue(filters?.mongoFilters || {}, field.key);
    if (value) {
      const pathParts = field.path.split(".");
      let cursor = mongo;
      pathParts.forEach((part, index) => {
        if (index === pathParts.length - 1) {
          cursor[part] = value;
          return;
        }
        cursor[part] = cursor[part] || {};
        cursor = cursor[part];
      });
    }
  });
  return { sites: ["VIG", "BOT"], moda, mongo };
};

const buildInitialFiltersFromCombination = (combination) => {
  const filters = {};
  CATEGORY_MAPPING_MODA_FIELDS.forEach((field) => {
    const value = getFirstFilledValue(combination?.moda?.[field.key]);
    filters[`${field.key}_eq`] = value;
    filters[`${field.key}_in`] = [];
    filters[`${field.key}_not_in`] = [];
  });
  return filters;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(Number(value || 0));

const normalizeRequirements = (requirements) =>
  Array.isArray(requirements)
    ? requirements
        .map((requirement) => ({
          source: requirement?.source,
          field: requirement?.field,
          path: requirement?.path,
          label: requirement?.label || requirement?.field || requirement?.path,
        }))
        .filter((requirement) => requirement.source && (requirement.field || requirement.path))
    : [];

const requirementKey = (requirement) =>
  `${requirement?.source || ""}:${requirement?.field || requirement?.path || ""}`;

const lookupDescription = (lookupOptions, fieldKey, value) => {
  const normalized = getFirstFilledValue(value);
  if (!normalized) return "";
  const option = (lookupOptions?.[fieldKey] || []).find(
    (item) => String(item.code) === normalized,
  );
  return option?.description || normalized;
};

const requirementSourceContextSummary = (source, lookupOptions) => {
  const parts = CATEGORY_MAPPING_MODA_FIELDS
    .map((field) => {
      const value = getFirstFilledValue(source?.context?.[field.key]);
      return value
        ? field.label + "=" + lookupDescription(lookupOptions, field.key, value)
        : "";
    })
    .filter(Boolean);
  return parts.length ? parts.join(" · ") : "Regola generale";
};

const RequirementSourceBoxes = ({ site, siteRequirement, lookupOptions, sourcesOverride, showSatisfied = false }) => {
  if (siteRequirement?.legacy) {
    return (
      <div className="ba br2 pa2 mb2 f7" style={{ borderColor: site.color }}>
        <div className="fw6 mb1">{site.key} · Configurazione legacy</div>
        <div>{siteRequirement.reason || "Requisiti da ridefinire"}</div>
      </div>
    );
  }
  const sources = (sourcesOverride || [
    ...(siteRequirement?.directSources || []),
    ...(siteRequirement?.inheritedSources || []),
  ]).filter((source) =>
    showSatisfied || normalizeRequirements(source?.missingRequirements).length > 0
  );
  if (!sources.length) return null;
  return (
    <div>
      {sources.map((source, index) => {
        const inherited = source.specificity === "inherited";
        const missing = normalizeRequirements(source.missingRequirements);
        const displayedRequirements = missing.length
          ? missing
          : normalizeRequirements(source.requirements);
        return (
          <div
            key={site.key + "-requirement-source-" + (source.id || source.combinationHash || index)}
            className="ba br2 pa2 mb2 f7"
            style={{
              borderColor: site.color,
              borderLeftWidth: "4px",
              backgroundColor: inherited ? "rgba(0, 0, 0, 0.025)" : "transparent",
            }}
          >
            <div className="fw6 mb1">
              {site.key} · {inherited ? "Ereditata" : "Diretta"}
            </div>
            <div className="gray mb1">{requirementSourceContextSummary(source, lookupOptions)}</div>
            <div>
              {missing.length ? "Richiede: " : "Gia soddisfatta: "}
              {displayedRequirements.map((item) => item.label).join(", ")}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const hasDirectSiteRequirements = (siteRequirement) =>
  Array.isArray(siteRequirement?.directSources)
    ? siteRequirement.directSources.length > 0
    : normalizeRequirements(siteRequirement?.requirements).length > 0;

const hasInheritedSiteRequirements = (siteRequirement) =>
  Array.isArray(siteRequirement?.inheritedSources) && siteRequirement.inheritedSources.length > 0;

const directSiteRequirements = (siteRequirement) =>
  Array.isArray(siteRequirement?.directRequirements)
    ? siteRequirement.directRequirements
    : siteRequirement?.requirements || [];

const requirementsActionLabel = (row) => {
  const siteRequirements = row?.siteRequirements || {};
  if (CATEGORY_SITES.some((site) => hasDirectSiteRequirements(siteRequirements[site.key]))) {
    return "Requisiti";
  }
  if (CATEGORY_SITES.some((site) => hasInheritedSiteRequirements(siteRequirements[site.key]))) {
    return "Aggiungi requisito specifico";
  }
  return "Segnala insufficiente";
};

const combinationModaValue = (combination, field) =>
  field?.source === "moda"
    ? getFirstFilledValue(combination?.moda?.[field.field])
    : "";

const isRequirementAlreadyDefinedByCombination = (combination, field) =>
  Boolean(combinationModaValue(combination, field));

const selectableRequirementsForCombination = (combination, requirements) =>
  normalizeRequirements(requirements).filter(
    (requirement) => !isRequirementAlreadyDefinedByCombination(combination, requirement),
  );

function WebCategoryMappingValuesPage({ token, utente, temaColori }) {
  const { buttonClassesGreen, buttonClassesWhite } = stilePulsanti;
  const canManage = Boolean(utente?.permessiAPI?.["update:prestashop"]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0, page: 1 });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [siteCategories, setSiteCategories] = useState({ VIG: [], BOT: [] });
  const [lookupOptions, setLookupOptions] = useState({});
  const [datiUlterioriStandard, setDatiUlterioriStandard] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCombination, setSelectedCombination] = useState(null);
  const [requirementsModalCombination, setRequirementsModalCombination] = useState(null);
  const [requirementsBySite, setRequirementsBySite] = useState({ VIG: [], BOT: [] });
  const [selectedRequirementSites, setSelectedRequirementSites] = useState({ VIG: false, BOT: false });
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingStatic, setLoadingStatic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const getAuth = useCategoryMappingAuth();
  const rowsRead = useRef(null);
  const staticRead = useRef(null);

  const modalInitialFilters = useMemo(
    () => buildInitialFiltersFromCombination(selectedCombination),
    [selectedCombination],
  );

  const loadStaticData = useCallback(async () => {
    staticRead.current?.abort();
    const controller = new AbortController();
    staticRead.current = controller;
    const { signal } = controller;
    setLoadingStatic(true);
    try {
      const authHeaders = { ...await getAuth(), signal };
      if (signal.aborted) return;
      const [vigResponse, botResponse, datiResponse, ...lookupResponses] = await Promise.all([
        axios.get(apiUrl + "/prodotti/categories/site-categories?site=VIG", authHeaders),
        axios.get(apiUrl + "/prodotti/categories/site-categories?site=BOT", authHeaders),
        axios.get(apiUrl + "/prodotti/get/datiUlteriori", authHeaders),
        ...CATEGORY_MAPPING_MODA_FIELDS.map((field) => axios.get(apiUrl + field.lookupUrl, authHeaders)),
      ]);
      if (signal.aborted) return;
      setSiteCategories({
        VIG: Array.isArray(vigResponse?.data?.data) ? vigResponse.data.data : [],
        BOT: Array.isArray(botResponse?.data?.data) ? botResponse.data.data : [],
      });
      setDatiUlterioriStandard(datiResponse?.data || {});
      setLookupOptions(
        Object.fromEntries(
          CATEGORY_MAPPING_MODA_FIELDS.map((field, index) => [
            field.key,
            normalizeLookupRecords(lookupResponses[index]?.data),
          ]),
        ),
      );
    } catch (loadError) {
      if (isCancelledRead(loadError, signal)) return;
      setError(loadError?.response?.data?.error || loadError.message || "Errore caricamento dati mapping");
    } finally {
      if (!signal.aborted) setLoadingStatic(false);
    }
  }, [getAuth]);

  const loadRows = useCallback(async () => {
    rowsRead.current?.abort();
    const controller = new AbortController();
    rowsRead.current = controller;
    const { signal } = controller;
    setLoadingRows(true);
    setError("");
    try {
      const authHeaders = await getAuth();
      if (signal.aborted) return;
      const response = await axios.get(apiUrl + "/prodotti/categories/attribute-rules/moda-combinations", {
        ...authHeaders,
        signal,
        params: {
          page: filters.page,
          perPage: filters.perPage,
          sortBy: filters.sortBy,
          sortDirection: filters.sortDirection,
          search: filters.search || undefined,
          publishedOnly: filters.publishedOnly ? "1" : undefined,
          missingMappingOnly: filters.missingMappingOnly ? "1" : undefined,
          defaultConflictsOnly: filters.defaultConflictsOnly ? "1" : undefined,
          includeIgnored: filters.includeIgnored ? "1" : undefined,
          minArticleCount: filters.minArticleCount || undefined,
          maxArticleCount: filters.maxArticleCount || undefined,
          minStock: filters.minStock || undefined,
          maxStock: filters.maxStock || undefined,
          reparto: filters.reparto || undefined,
          categoria: filters.categoria || undefined,
          tipo: filters.tipo || undefined,
          sottotipo: filters.sottotipo || undefined,
          persona: filters.persona || undefined,
        },
      });
      if (signal.aborted) return;
      setRows(Array.isArray(response?.data?.data) ? response.data.data : []);
      setMeta(response?.data?.meta || { total: 0, totalPages: 0, page: filters.page });
    } catch (loadError) {
      if (isCancelledRead(loadError, signal)) return;
      setError(loadError?.response?.data?.error || loadError.message || "Errore caricamento valori da mappare");
    } finally {
      if (!signal.aborted) setLoadingRows(false);
    }
  }, [getAuth, filters]);

  useEffect(() => {
    loadStaticData();
    return () => staticRead.current?.abort();
  }, [loadStaticData]);

  useEffect(() => {
    loadRows();
    return () => rowsRead.current?.abort();
  }, [loadRows]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: key === "page" ? value : 1 }));
  };

  const openMappingModal = (combination) => {
    setSelectedCombination(combination);
    setModalOpen(true);
    setMessage("");
    setError("");
  };

  const openRequirementsModal = (combination) => {
    const current = combination?.siteRequirements || {};
    setRequirementsModalCombination(combination);
    setRequirementsBySite({
      VIG: selectableRequirementsForCombination(combination, directSiteRequirements(current?.VIG)),
      BOT: selectableRequirementsForCombination(combination, directSiteRequirements(current?.BOT)),
    });
    setSelectedRequirementSites({
      VIG: hasDirectSiteRequirements(current?.VIG),
      BOT: hasDirectSiteRequirements(current?.BOT),
    });
    setError("");
    setMessage("");
  };

  const toggleRequirementSite = (site) => {
    setSelectedRequirementSites((previous) => ({
      ...previous,
      [site]: !previous[site],
    }));
  };

  const toggleRequirement = (site, field) => {
    if (isRequirementAlreadyDefinedByCombination(requirementsModalCombination, field)) return;
    const inheritedRequirements = normalizeRequirements(
      requirementsModalCombination?.siteRequirements?.[site]?.inheritedRequirements,
    );
    if (inheritedRequirements.some((requirement) => requirementKey(requirement) === requirementKey(field))) return;
    setRequirementsBySite((previous) => {
      const current = normalizeRequirements(previous?.[site]);
      const fieldKey = requirementKey(field);
      const selected = current.some((requirement) => requirementKey(requirement) === fieldKey);
      return {
        ...previous,
        [site]: selected
          ? current.filter((requirement) => requirementKey(requirement) !== fieldKey)
          : [...current, { source: field.source, field: field.field, path: field.path, label: field.label }],
      };
    });
  };

  const saveRequirements = async () => {
    if (!canManage || !requirementsModalCombination) return;
    const requirementUsername = getFirstFilledValue(utente?.username);
    if (!isValidAuditUsername(requirementUsername)) {
      setError("Username utente mancante o non valido: aggiorna la sessione prima di salvare.");
      return;
    }
    const currentRequirements = requirementsModalCombination.siteRequirements || {};
    const sitesToUpdate = CATEGORY_SITES
      .map((site) => site.key)
      .filter((site) => selectedRequirementSites[site] || hasDirectSiteRequirements(currentRequirements[site]));
    const selectedWithoutFields = sitesToUpdate.find(
      (site) => selectedRequirementSites[site] && selectableRequirementsForCombination(
        requirementsModalCombination,
        requirementsBySite[site],
      ).length === 0,
    );
    if (selectedWithoutFields) {
      setError(`Indica almeno un dato necessario per ${selectedWithoutFields}.`);
      return;
    }
    if (!sitesToUpdate.length) {
      setRequirementsModalCombination(null);
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules/moda-combinations/requirements",
        {
          username: requirementUsername,
          moda: requirementsModalCombination?.moda || {},
          sites: sitesToUpdate,
          requirementsBySite: Object.fromEntries(
            sitesToUpdate.map((site) => [
              site,
              selectedRequirementSites[site]
                ? selectableRequirementsForCombination(requirementsModalCombination, requirementsBySite[site])
                : [],
            ]),
          ),
        },
        await getAuth(),
      );
      setRequirementsModalCombination(null);
      setMessage("Requisiti di attendibilita aggiornati per sito.");
      await loadRows();
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError.message || "Errore aggiornamento requisiti");
    } finally {
      setSaving(false);
    }
  };

  const resolveExistingMappings = useCallback(
    async (modalFilters, options = {}) => {
      const response = await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules/resolve",
        buildCategoryMappingResolveInput(modalFilters),
        { ...await getAuth(), signal: options.signal },
      );
      return response?.data?.data || {};
    },
    [getAuth],
  );

  const saveMappingFromModal = async (payload) => {
    const mappingUsername = getFirstFilledValue(utente?.username);
    if (!isValidAuditUsername(mappingUsername)) {
      setError("Username utente mancante o non valido: aggiorna la sessione prima di salvare.");
      return;
    }
    if (!canManage) return;
    const modalFilters = payload?.filters || {};
    const mappingName = getFirstFilledValue(payload?.categoryMappingName);
    const categoryOutputs = payload?.categoryOutputs || {};
    const categoryDefaultOutputs = payload?.categoryDefaultOutputs || {};
    const conditions = buildCategoryMappingConditions(modalFilters);
    const missingSites = missingDefaultSites(categoryOutputs, categoryDefaultOutputs);
    if (missingSites.length) {
      setError(`Scegliere la categoria predefinita per il sito ${missingSites.join(", ")}.`);
      return;
    }
    const outputs = ["VIG", "BOT"].flatMap((site) => {
      const siteOutputs = Array.isArray(categoryOutputs?.[site])
        ? categoryOutputs[site]
        : [];
      const defaultOutput = categoryDefaultOutputs?.[site] || "";
      return siteOutputs.map((externalCategoryId, index) => ({
        site,
        externalCategoryId,
        isDefaultCandidate: String(externalCategoryId) === String(defaultOutput),
        priority: index,
      }));
    });

    if (!conditions.length) {
      setError("Seleziona almeno un requisito MODA o Mongo per creare la mappatura.");
      return;
    }
    if (!outputs.length) {
      setError("Seleziona almeno una categoria VIG o BOT come output.");
      return;
    }

    const modaLabel = CATEGORY_MAPPING_MODA_FIELDS.map((field) => selectedCombination?.moda?.[field.key] || "-").join(" / ");
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules",
        {
          username: mappingUsername,
          ruleKey: "",
          description: mappingName || `Mappatura categorie per combinazione MODA ${modaLabel}`,
          priority: 0,
          active: true,
          isDefaultCandidate: false,
          conditionsJson: JSON.stringify(conditions, null, 2),
          outputs,
        },
        await getAuth(),
      );
      setMessage("Mappatura creata.");
      setModalOpen(false);
      setSelectedCombination(null);
      await loadRows();
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError.message || "Errore salvataggio mappatura");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={"pa3 " + (temaColori || "")}>
      <div className="sfondoBiancoTema pa3 br3">
        <div className="flex flex-wrap items-start justify-between mb3">
          <div>
            <h2 className="ma0">Valori da mappare</h2>
            <div className="f6 mt1">
              Combinazioni MODA realmente presenti negli articoli e mapping categorie web applicabili.
            </div>
            <div className="f7 gray mt2">
              Totale combinazioni: {formatNumber(meta.total)}
            </div>
          </div>
          <button type="button" className={buttonClassesWhite} onClick={loadRows} disabled={loadingRows}>
            Aggiorna
          </button>
        </div>

        {error && <div className="pa3 mb3 bg-washed-red dark-red">{error}</div>}
        {message && <div className="pa3 mb3 bg-washed-green dark-green">{message}</div>}
        {!canManage && <div className="pa3 mb3 bg-washed-yellow">Modalita lettura: per modificare serve update:prestashop.</div>}

        <div className="flex flex-wrap items-end mb3 nl2 nr2">
          <label className="db ph2 mb2">
            <span className="db f7 fw6 mb1">Cerca valori</span>
            <input
              className="input-reset ba b--black-20 pa2 br2"
              type="text"
              value={filters.search}
              onChange={(event) => setFilter("search", event.target.value)}
              placeholder="Reparto, categoria, tipo..."
            />
          </label>
          {CATEGORY_MAPPING_MODA_FIELDS.map((field) => (
            <label key={field.key} className="db ph2 mb2">
              <span className="db f7 fw6 mb1">{field.label}</span>
              <select
                className="ba b--black-20 pa2 br2 mw5"
                value={filters[field.key]}
                onChange={(event) => setFilter(field.key, event.target.value)}
              >
                <option value="">Tutti</option>
                <option value={EMPTY_MODA_COMBINATION_FILTER}>Non valorizzato</option>
                {(lookupOptions[field.key] || []).map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.description === option.code ? option.description : `${option.description} (${option.code})`}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <label className="db ph2 mb2">
            <span className="db f7 fw6 mb1">Articoli min</span>
            <input className="input-reset ba b--black-20 pa2 br2 w4" type="number" value={filters.minArticleCount} onChange={(event) => setFilter("minArticleCount", event.target.value)} />
          </label>
          <label className="db ph2 mb2">
            <span className="db f7 fw6 mb1">Articoli max</span>
            <input className="input-reset ba b--black-20 pa2 br2 w4" type="number" value={filters.maxArticleCount} onChange={(event) => setFilter("maxArticleCount", event.target.value)} />
          </label>
          <label className="db ph2 mb2">
            <span className="db f7 fw6 mb1">Stock min</span>
            <input className="input-reset ba b--black-20 pa2 br2 w4" type="number" value={filters.minStock} onChange={(event) => setFilter("minStock", event.target.value)} />
          </label>
          <label className="db ph2 mb2">
            <span className="db f7 fw6 mb1">Stock max</span>
            <input className="input-reset ba b--black-20 pa2 br2 w4" type="number" value={filters.maxStock} onChange={(event) => setFilter("maxStock", event.target.value)} />
          </label>
          <label className="db ph2 mb2">
            <span className="db f7 fw6 mb1">Ordina per</span>
            <select className="ba b--black-20 pa2 br2" value={filters.sortBy} onChange={(event) => setFilter("sortBy", event.target.value)}>
              <option value="articleCount">Numero articoli</option>
              <option value="totalStock">Stock complessivo</option>
              <option value="reparto">Reparto</option>
              <option value="categoria">Categoria</option>
              <option value="tipo">Tipo</option>
              <option value="sottotipo">Sottotipo</option>
              <option value="persona">Persona</option>
            </select>
          </label>
          <label className="db ph2 mb2">
            <span className="db f7 fw6 mb1">Direzione</span>
            <select className="ba b--black-20 pa2 br2" value={filters.sortDirection} onChange={(event) => setFilter("sortDirection", event.target.value)}>
              <option value="desc">Decrescente</option>
              <option value="asc">Crescente</option>
            </select>
          </label>
          <label className="db ph2 mb2 pointer f6">
            <input type="checkbox" checked={filters.publishedOnly} onChange={(event) => setFilter("publishedOnly", event.target.checked)} /> Solo pubblicato SI
          </label>
          <label className="db ph2 mb2 pointer f6">
            <input type="checkbox" checked={filters.missingMappingOnly} onChange={(event) => setFilter("missingMappingOnly", event.target.checked)} /> Map mancanti
          </label>
          <label className="dib mr3 pointer f6">
            <input type="checkbox" checked={filters.defaultConflictsOnly} onChange={(event) => setFilter("defaultConflictsOnly", event.target.checked)} /> Solo default doppi
          </label>
          <label className="db ph2 mb2 pointer f6">
            <input type="checkbox" checked={filters.includeIgnored} onChange={(event) => setFilter("includeIgnored", event.target.checked)} /> Mostra troppo generiche
          </label>
        </div>

        <div className="mb3 f7 gray">
          Legenda: <span className="ph2 pv1 br-pill white" style={{ backgroundColor: "#174f88" }}>VIG specifica</span>{" "}
          <span className="ph2 pv1 br-pill" style={{ backgroundColor: "#cfe4fb", color: "#16426d" }}>VIG ereditata</span>{" "}
          <span className="ph2 pv1 br-pill white" style={{ backgroundColor: "#8b1e1e" }}>BOT specifica</span>{" "}
          <span className="ph2 pv1 br-pill" style={{ backgroundColor: "#f5cccc", color: "#5f1717" }}>BOT ereditata</span>
        </div>

        {loadingRows || loadingStatic ? (
          <div className="pa3 ba b--black-10">Caricamento...</div>
        ) : rows.length === 0 ? (
          <div className="pa3 ba b--black-10">Nessuna combinazione trovata.</div>
        ) : (
          <div className="overflow-auto">
            <table className="collapse w-100 f6">
              <thead>
                <tr className="tl bb b--black-10">
                  {CATEGORY_MAPPING_MODA_FIELDS.map((field) => (
                    <th key={field.key} className="pa2">{field.label}</th>
                  ))}
                  <th className="pa2 tr">Articoli</th>
                  <th className="pa2 tr">Stock</th>
                  <th className="pa2">Mapping</th>
                  <th className="pa2">Stato</th>
                  <th className="pa2 tr">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key} className="bb b--black-05 v-top">
                    {CATEGORY_MAPPING_MODA_FIELDS.map((field) => (
                      <td key={field.key} className="pa2 nowrap">{row.moda?.[field.key] || <span className="gray">-</span>}</td>
                    ))}
                    <td className="pa2 tr fw6">{formatNumber(row.articleCount)}</td>
                    <td className="pa2 tr fw6">{formatNumber(row.totalStock)}</td>
                    <td className="pa2" style={{ minWidth: "18rem" }}>
                      {Array.isArray(row.mappings) && row.mappings.length ? (
                        <WebCategoryMappingCards mappings={row.mappings} rowKey={row.key} defaultConflicts={row.defaultConflicts} combinationKey={row.key} />
                      ) : (
                        <span className="gray">Nessuna map</span>
                      )}
                    </td>
                    <td className="pa2" style={{ minWidth: "17rem" }}>
                      <div>
                        {CATEGORY_SITES.map((site) => (
                          <RequirementSourceBoxes
                            key={row.key + "-" + site.key}
                            site={site}
                            siteRequirement={row.siteRequirements?.[site.key]}
                            lookupOptions={lookupOptions}
                          />
                        ))}
                        {(row.unresolvedSites || []).map((site) => (
                          <span
                            key={row.key + "-unresolved-" + site}
                            className="db br-pill ph2 pv1 mb1 f7 bg-washed-yellow dark-red ba b--gold"
                          >
                            {site}: da gestire
                          </span>
                        ))}
                        {Object.keys(row.defaultConflicts || {}).map((site) => (
                          <span key={site} className="db scrittaRosso fw6">{site}: Default doppio</span>
                        ))}
                        {(row.unresolvedSites || []).length === 0 && Object.keys(row.defaultConflicts || {}).length === 0 && (
                          <span className="dark-green fw6">Gestita</span>
                        )}
                      </div>
                    </td>
                    <td className="pa2 tr nowrap">
                      <button
                        type="button"
                        className={buttonClassesGreen}
                        disabled={!canManage || saving}
                        onClick={() => openMappingModal(row)}
                      >
                        Crea map
                      </button>
                      <button
                        type="button"
                        className={buttonClassesWhite + " ml2"}
                        disabled={!canManage || saving}
                        onClick={() => openRequirementsModal(row)}
                      >
                        {requirementsActionLabel(row)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between mt3">
          <button type="button" className={buttonClassesWhite} disabled={filters.page <= 1 || loadingRows} onClick={() => setFilter("page", Math.max(1, Number(filters.page) - 1))}>
            Precedente
          </button>
          <div className="f7 gray">Pagina {meta.page || filters.page} di {meta.totalPages || 1}</div>
          <button type="button" className={buttonClassesWhite} disabled={Number(filters.page) >= Number(meta.totalPages || 1) || loadingRows} onClick={() => setFilter("page", Number(filters.page) + 1)}>
            Successiva
          </button>
        </div>
      </div>

      {requirementsModalCombination && (
        <div className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema flex items-center justify-center">
          <div className="sfondoBiancoTema black-80 w-90 w-60-l pa4 br2 shadow-5" style={{ maxHeight: "88vh", overflowY: "auto" }}>
            <div className="flex justify-between items-start mb3">
              <div>
                <h3 className="mt0 mb1">Dati necessari per una mappatura attendibile</h3>
                <div className="f7 gray">Definisci il requisito per ogni sito che non puo usare la combinazione MODA da sola.</div>
              </div>
              <button type="button" className={buttonClassesWhite} onClick={() => setRequirementsModalCombination(null)}>Chiudi</button>
            </div>
            {CATEGORY_SITES.map((site) => {
              const selected = Boolean(selectedRequirementSites[site.key]);
              const selectedFields = normalizeRequirements(requirementsBySite[site.key]);
              const siteRequirement = requirementsModalCombination.siteRequirements?.[site.key];
              const inheritedSources = siteRequirement?.inheritedSources || [];
              const inheritedRequirementKeys = new Set(
                normalizeRequirements(siteRequirement?.inheritedRequirements).map(requirementKey),
              );
              return (
                <section key={site.key} className="mb3 pb3 bb b--black-10">
                  {inheritedSources.length > 0 && (
                    <div className="mb3">
                      <div className="f7 fw6 mb2">Requisiti ereditati, modificabili dalla combinazione di origine</div>
                      <RequirementSourceBoxes
                        site={site}
                        siteRequirement={siteRequirement}
                        lookupOptions={lookupOptions}
                        sourcesOverride={inheritedSources}
                        showSatisfied
                      />
                    </div>
                  )}
                  <label className="flex items-center pointer fw6 mb2">
                    <input type="checkbox" className="mr2" checked={selected} onChange={() => toggleRequirementSite(site.key)} />
                    {site.label}: la mappatura e troppo generica
                  </label>
                  {selected && (
                    <div className="pl3">
                      <div className="f7 gray mb2">Seleziona i dati che devono essere valorizzati prima di considerare attendibile la mappatura.</div>
                      <div className="flex flex-wrap">
                        {CATEGORY_MAPPING_REQUIREMENT_FIELDS.map((field) => {
                          const definedValue = combinationModaValue(requirementsModalCombination, field);
                          const alreadyDefined = Boolean(definedValue);
                          const checked = selectedFields.some((requirement) => requirementKey(requirement) === requirementKey(field));
                          const inherited = inheritedRequirementKeys.has(requirementKey(field));
                          if (inherited) {
                            return (
                              <div key={site.key + "-" + field.key} className="w-50-l w-100 pr2 mb2 f6">
                                <div className="pa2 br2 ba b--black-20 bg-near-white gray">
                                  <span className="fw6">{field.label}</span>
                                  <span className="ml2 f7">
                                    Ereditato{alreadyDefined ? ", gia definito: " + definedValue : ""}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          if (alreadyDefined) {
                            return (
                              <div
                                key={`${site.key}-${field.key}`}
                                className="w-50-l w-100 pr2 mb2 f6"
                                aria-label={`${field.label} gia definito: ${definedValue}`}
                              >
                                <div className="pa2 br2 bg-washed-green dark-green ba b--green">
                                  <span className="fw6">{field.label}</span>
                                  <span className="ml2 f7">Gia definito: {definedValue}</span>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <label key={`${site.key}-${field.key}`} className="w-50-l w-100 pr2 mb2 pointer f6">
                              <input type="checkbox" className="mr2" checked={checked} onChange={() => toggleRequirement(site.key, field)} />
                              {field.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
            <div className="flex justify-end mt3">
              <button type="button" className={buttonClassesWhite + " mr2"} onClick={() => setRequirementsModalCombination(null)} disabled={saving}>Annulla</button>
              <button type="button" className={buttonClassesGreen} onClick={saveRequirements} disabled={saving}>Salva requisiti</button>
            </div>
          </div>
        </div>
      )}

      <ModaArticoliAdvancedFiltersModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCombination(null);
        }}
        onApply={saveMappingFromModal}
        initialFilters={modalInitialFilters}
        baselineFilters={modalInitialFilters}
        lookupOptions={lookupOptions}
        datiUlterioriStandard={datiUlterioriStandard}
        temaColori={temaColori || "rootTema"}
        categoryMappingMode
        categoryMappingSaveError={error}
        siteCategories={siteCategories}
        initialCategoryMappingOutputs={{ VIG: [], BOT: [] }}
        onResolveExistingMappings={resolveExistingMappings}
      />
    </div>
  );
}

export default WebCategoryMappingValuesPage;
